"use client";

import {
  type ElementType,
  type FC,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "@/components/ui/link";
import { useStableId } from "@/lib/use-stable-id";
import { cn } from "@/lib/utils";
import { UserMenuContext, type UserMenuContextValue, useUserMenu } from "./user-menu.context";

export interface UserMenuProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType;
}

/** Closes the menu on a pointer down outside the root. */
const Root: FC<UserMenuProps> = ({ as: Tag = "div", className, children, ...props }) => {
  const [open, setOpen] = useState(false);
  const menuId = useStableId();
  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const contextValue: UserMenuContextValue = { menuId, open, setOpen };

  return (
    <UserMenuContext.Provider value={contextValue}>
      <Tag ref={rootRef} className={cn("relative inline-flex", className)} {...props}>
        {children}
      </Tag>
    </UserMenuContext.Provider>
  );
};

interface PartProps {
  className?: string;
  children?: ReactNode;
}

interface AvatarProps extends PartProps {
  label?: string;
  src?: string;
}

/**
 * Toggles the menu; Escape closes it and brings the focus back here.
 * Shows the photo in `src`, and the initials in `children` when there is none or it fails to load.
 */
const Avatar: FC<AvatarProps> = ({ label = "Menu da conta", src, className, children }) => {
  const { menuId, open, setOpen } = useUserMenu();
  const ref = useRef<HTMLButtonElement>(null);
  const [failedSrc, setFailedSrc] = useState<string>();
  const showPhoto = src !== undefined && src !== failedSrc;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      ref.current?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, setOpen]);

  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      aria-expanded={open}
      aria-controls={menuId}
      onClick={() => setOpen(!open)}
      className={cn(
        "flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-accent font-display text-h6 font-bold text-on-accent cursor-pointer",
        className,
      )}
    >
      {showPhoto ? (
        // biome-ignore lint/performance/noImgElement: the image adapter has no `onError`, which the initials fallback needs.
        <img
          src={src}
          alt=""
          onError={() => setFailedSrc(src)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span aria-hidden>{children}</span>
      )}
    </button>
  );
};

const Menu: FC<PartProps> = ({ className, children }) => {
  const { menuId, open } = useUserMenu();

  return (
    <ul
      id={menuId}
      hidden={!open}
      className={cn(
        "absolute top-full right-0 z-50 mt-2 w-52 surface-card p-2 shadow-lg",
        className,
      )}
    >
      {children}
    </ul>
  );
};

const ENTRY_CLASS_NAME =
  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-body-sm font-medium text-fg-strong no-underline hover:bg-bg-secondary hover:no-underline cursor-pointer";

const EntryIcon: FC<{ children: ReactNode }> = ({ children }) => (
  <span aria-hidden className="flex shrink-0 [&>svg]:h-4 [&>svg]:w-4">
    {children}
  </span>
);

interface ItemProps extends PartProps {
  href: string;
  icon?: ReactNode;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

const Item: FC<ItemProps> = ({ href, icon, onClick, className, children }) => {
  const { setOpen } = useUserMenu();

  return (
    <li>
      <Link
        href={href}
        className={cn(ENTRY_CLASS_NAME, className)}
        onClick={(e) => {
          setOpen(false);
          onClick?.(e);
        }}
      >
        {icon && <EntryIcon>{icon}</EntryIcon>}
        {children}
      </Link>
    </li>
  );
};

interface ActionProps extends PartProps {
  icon?: ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
}

const Action: FC<ActionProps> = ({ icon, onClick, className, children }) => {
  const { setOpen } = useUserMenu();

  return (
    <li>
      <button
        type="button"
        className={cn(ENTRY_CLASS_NAME, className)}
        onClick={(e) => {
          setOpen(false);
          onClick?.(e);
        }}
      >
        {icon && <EntryIcon>{icon}</EntryIcon>}
        {children}
      </button>
    </li>
  );
};

Root.displayName = "UserMenu";
Avatar.displayName = "UserMenu.Avatar";
Menu.displayName = "UserMenu.Menu";
Item.displayName = "UserMenu.Item";
Action.displayName = "UserMenu.Action";

export const UserMenu = Object.assign(Root, { Avatar, Menu, Item, Action });
